import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/categories";

const navLinks = [
  { label: "首页", to: "/" },
  { label: "分类", to: "/category/xuanhuan", isCategory: true },
  { label: "搜索", to: "/search" },
  { label: "关于", to: "/about" },
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);

  useEffect(() => {
    setMobileOpen(false);
    setCategoryOpen(false);
  }, [location.pathname, location.search]);

  const submitSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const q = keyword.trim();
    if (!q) {
      navigate("/search");
      return;
    }
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  const isActive = (to: string) => {
    if (to === "/") return location.pathname === "/";
    if (to.startsWith("/category")) return location.pathname.startsWith("/category");
    return location.pathname.startsWith(to);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="w-full px-4 md:px-8">
        <div className="flex h-16 items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 shrink-0">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-book-2-fill text-xl"></i>
            </span>
            <span className="flex flex-col leading-tight">
              <span className="font-heading text-xl font-bold tracking-wide text-foreground-950">
                御书屋
              </span>
              <span className="hidden text-[11px] text-foreground-500 sm:block">
                高清小说阅读平台
              </span>
            </span>
          </Link>

          <nav className="hidden items-center gap-1 md:flex">
            {navLinks.map((link) =>
              link.isCategory ? (
                <div
                  key={link.to}
                  className="relative"
                  onMouseEnter={() => setCategoryOpen(true)}
                  onMouseLeave={() => setCategoryOpen(false)}
                >
                  <Link
                    to={link.to}
                    className={`flex items-center gap-1 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                      isActive(link.to)
                        ? "text-primary-600"
                        : "text-foreground-700 hover:text-primary-600"
                    }`}
                  >
                    {link.label}
                    <i className="ri-arrow-down-s-line text-base"></i>
                  </Link>
                  {categoryOpen && (
                    <div className="absolute left-0 top-full w-64 pt-2">
                      <div className="grid grid-cols-2 gap-1 rounded-lg border border-background-200 bg-background-50 p-2">
                        {categories.map((category) => (
                          <Link
                            key={category.slug}
                            to={`/category/${category.slug}`}
                            className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-600"
                          >
                            <i className={`${category.icon} text-base text-accent-500`}></i>
                            {category.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                    isActive(link.to)
                      ? "text-primary-600"
                      : "text-foreground-700 hover:text-primary-600"
                  }`}
                >
                  {link.label}
                </Link>
              ),
            )}
          </nav>

          <div className="flex items-center gap-2">
            <form
              onSubmit={submitSearch}
              className="hidden items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3 py-1.5 lg:flex"
            >
              <span className="flex h-5 w-5 items-center justify-center text-foreground-400">
                <i className="ri-search-line text-base"></i>
              </span>
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索书名 / 作者 / 分类"
                className="w-44 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
              <button
                type="submit"
                className="whitespace-nowrap rounded-full bg-accent-500 px-3 py-1 text-xs font-medium text-background-50 transition-colors hover:bg-accent-600"
              >
                搜索
              </button>
            </form>

            <button
              type="button"
              aria-label="打开菜单"
              onClick={() => setMobileOpen((prev) => !prev)}
              className="flex h-10 w-10 items-center justify-center rounded-md border border-background-300 text-foreground-700 md:hidden"
            >
              <i className={mobileOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"}></i>
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="border-t border-background-200 bg-background-50 md:hidden">
          <div className="px-4 py-3">
            <form
              onSubmit={submitSearch}
              className="mb-3 flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3 py-2"
            >
              <span className="flex h-5 w-5 items-center justify-center text-foreground-400">
                <i className="ri-search-line text-base"></i>
              </span>
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索书名 / 作者 / 分类"
                className="flex-1 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
              <button
                type="submit"
                className="whitespace-nowrap rounded-full bg-accent-500 px-3 py-1 text-xs font-medium text-background-50"
              >
                搜索
              </button>
            </form>
            <div className="flex flex-col">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="rounded-md px-3 py-2.5 text-sm font-medium text-foreground-800 hover:bg-background-100"
                >
                  {link.label}
                </Link>
              ))}
            </div>
            <div className="mt-2 grid grid-cols-2 gap-1 border-t border-background-200 pt-3">
              {categories.map((category) => (
                <Link
                  key={category.slug}
                  to={`/category/${category.slug}`}
                  className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-700 hover:bg-background-100"
                >
                  <i className={`${category.icon} text-base text-accent-500`}></i>
                  {category.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}